import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import queryString from 'query-string';

import {
  getBankingInfoFile,
  getEntityInfoFile,
  getFullUserProfile,
} from 'redux/reducers/accountSlice';
import {
  FullBOUserResponseModel,
  ResourceDownloadResponse,
  ServiceAgreementTypeEnum,
} from 'api/backofficeApiClient';
import PrimaryInfo from './PrimaryInfo';
import AdditionalInfo from './AdditionalInfo';
import { CompanyProfileSection } from './enums';
import MemberProgressBar from './MemberProgressBar';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import useQueryMatch from 'hooks/useQueryMatch';
import BaseAlert from 'components/wrappers/BaseAlert';
import { isCorporateTASelector } from 'redux/selectors/authenticationSelectors';
import { LoadRecruiterDetailsOptions } from 'features/recruiter/RecruiterDetailsPage/types';
import MemberLogo from 'components/MemberLogo';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import { getUserDetails } from 'redux/reducers/authenticationSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './CompanyAccountPage.module.less';
import DownloadClientAgreement from '../client/ClientDetailsPage/DownloadClientAgreement';
import { DownloadAgreementTypeEnum } from '../client/ClientDetailsPage/DownloadClientAgreement/DownloadClientAgreement';

const DEFAULT_SECTION = CompanyProfileSection.PrimaryInfo;

const getPageSections = (isCorporateTA?: boolean) => {
  return isCorporateTA
    ? Object.values(CompanyProfileSection)
    : [DEFAULT_SECTION];
};

export default function CompanyProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const sectionQuery = queryString.parse(location.search);
  const dispatch = useAppDispatch();
  const isCorporateTA = useAppSelector(isCorporateTASelector);
  const [fullProfile, setFullProfile] = useState<FullBOUserResponseModel>();
  const [bankingInfoFile, setBankingInfoFile] =
    useState<ResourceDownloadResponse>();
  const [entityInfoFile, setEntityInfoFile] =
    useState<ResourceDownloadResponse>();
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);
  const pageSectionMatch = useQueryMatch(getPageSections(isCorporateTA));

  useEffect(() => {
    if (!pageSectionMatch) {
      navigate(`${location.pathname}?section=${DEFAULT_SECTION}`, {
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSectionMatch]);

  const loadBankingInfoFile = useCallback(async () => {
    const result = await dispatch(getBankingInfoFile());

    if (getBankingInfoFile.fulfilled.match(result)) {
      setBankingInfoFile(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  }, [dispatch]);

  const loadEntityInfoFile = useCallback(async () => {
    const result = await dispatch(getEntityInfoFile());

    if (getEntityInfoFile.fulfilled.match(result)) {
      setEntityInfoFile(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  }, [dispatch]);

  const loadFullProfile = useCallback(
    async (options?: LoadRecruiterDetailsOptions) => {
      const result = await dispatch(getFullUserProfile());

      if (getFullUserProfile.fulfilled.match(result)) {
        if (
          !!result.payload?.businessInfo?.entityInformation?.entityInfoFileKey
        ) {
          options?.shouldLoadEntityFile && (await loadEntityInfoFile());
        } else {
          setEntityInfoFile(undefined);
        }
        if (!!result.payload?.bankingInformation?.bankInfoFileKey) {
          options?.shouldLoadBankingFile && (await loadBankingInfoFile());
        } else {
          setBankingInfoFile(undefined);
        }

        await dispatch(getUserDetails());

        setFullProfile(result.payload);
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }

      setIsInitialLoaded(true);
    },
    [dispatch, loadEntityInfoFile, loadBankingInfoFile],
  );

  useEffect(() => {
    loadFullProfile({
      shouldLoadEntityFile: true,
      shouldLoadBankingFile: true,
    });
  }, [loadFullProfile]);

  if (!isInitialLoaded) return <Spin size="large" />;

  if (!fullProfile) return null;

  const { corporateInfo, businessInfo, memberLogoUrl, userId, companyId } =
    fullProfile;

  const getCompanyProfileSection = () => {
    switch (sectionQuery.section) {
      case CompanyProfileSection.AdditionalInfo:
        return <AdditionalInfo corporateInfo={corporateInfo} />;

      case CompanyProfileSection.PrimaryInfo:
      default:
        return (
          <PrimaryInfo
            fullProfile={fullProfile}
            loadFullProfile={loadFullProfile}
            entityInfoFile={entityInfoFile}
            bankingInfoFile={bankingInfoFile}
          />
        );
    }
  };

  return (
    <div className="page-wrapper scroll-box">
      <div className="page-holder">
        {!!fullProfile && (
          <div className={styles.progressBarWrapper}>
            <MemberProgressBar member={fullProfile} onChange={setFullProfile} />
          </div>
        )}
        <BasePageLabel label="Company profile" />

        <div className="page-box page-inner">
          <div className="page-inner-container">
            <div className="page-head align-start">
              <div className="page-head__left align-start">
                <div className="page-head__logo">
                  <MemberLogo
                    url={memberLogoUrl}
                    userId={userId}
                    companyId={companyId}
                  />
                </div>
                <div className="page-title page-head__title">
                  {businessInfo?.name}
                </div>
              </div>
              {fullProfile.businessInfo?.priceAgreement?.supportingFiles
                ?.length ? (
                <div>
                  <DownloadClientAgreement
                    listOfFiles={
                      fullProfile.businessInfo?.priceAgreement?.supportingFiles
                    }
                    id={userId}
                    agreementDetailsType={
                      ServiceAgreementTypeEnum.MemberAgreement
                    }
                    downloadAgreementType={DownloadAgreementTypeEnum.Member}
                  />
                </div>
              ) : (
                <></>
              )}
            </div>
            {getCompanyProfileSection()}
          </div>
        </div>
      </div>
    </div>
  );
}
